<script>
  import { onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Button, ButtonGroup, Input } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';
  import { QueryParams } from '@client/components/query-params';
  import { Select } from '@client/components/select';
  import { Switch } from '@client/components/switch';

  import { AssetsService } from '@client/services/assets';
  import { CampaignsService } from '@client/services/campaigns';

  import { defaultCampaignParams } from '@client/stores/domain-settings';

  import { AD_TYPES, LINK_TYPES } from '@client/enums';

  import { deepCopyArray } from '@common/utils/arrays';
  import { formatBytes, secondsToHms } from '@common/utils/numbers';
  import { isValidUrl } from '@common/utils/validation';

  export let linktype = LINK_TYPES.url.id;
  export let adtype = AD_TYPES.image.id;
  export let url = '';
  export let textString = '';
  export let textCss = '';
  export let fileData = {};
  export let files = [];
  export let alttext = '';
  export let validationError = false;
  export let selectedDocuments = '';
  export let selectedVideos = '';
  export let inline = false;
  export let is2x = false;
  export let isPlaceholder = false;
  export let countInternal = false;
  export let disableViews = false;

  let defaultParams = $defaultCampaignParams || [];
  let params = deepCopyArray(defaultParams);

  let errorMessage = '';
  let documentList = [];
  let documentItems = [];
  let videoList = [];
  let videoItems = [];

  const onUrlChange = () => {
    if (!url) {
      return;
    }

    const urlObj = new URL(url);
    const searchParams = new URLSearchParams(urlObj.search);

    params = [];

    if (searchParams.size > 0) {
      searchParams.forEach((value, name) => {
        const findIndex = params.findIndex((param) => param.name === name);
        if (findIndex === -1) {
          params.push({ name, value });
        } else {
          params[findIndex].value = value;
        }
      });

      params = [...params];
    }

    return url;
  };

  const onParamsChange = (items, removed) => {
    const hasParams = url.indexOf('?') > -1;

    let newUrl = hasParams ? url.slice(0, url.indexOf('?') + 1) : url + '?';

    items.map(({ name, value }) => {
      newUrl += `${name}=${value}&`;
    });

    url = newUrl.slice(0, -1);
  };

  const onClickAction = (e) => {
    url = '';
    selectedDocuments = '';
    selectedVideos = '';

    linktype = e.target.innerText.toLowerCase();

    if (linktype === $format(LINK_TYPES.doc.label).toLowerCase()) {
      linktype = LINK_TYPES.doc.id;
    }
  };

  const onAdType = (e) => {
    textString = '';
    textCss = '';
    files = [];
    alttext = '';

    adtype = e.target.innerText.toLowerCase();
  };

  const onValidation = () => {
    validationError = !isValidUrl(url);

    if (validationError) {
      errorMessage = 'label.VALIDATION_ERROR_NO_VALID_URL_CAMPAIGNS';
    }
  };

  const onFileUpload = async () => {
    const file = new FormData();
    file.append('file', files[0]);

    const response = await AssetsService.uploadFile(files[0].name, files[0].type, file);

    if (response?.status >= 400) {
      notify($format('label.FAILURE.ASSET_UPLOAD', { values: { asset: file.filename } }), 'error');

      return;
    }

    const { hash, filesize, filename, metadata } = response;

    fileData = { ...fileData, hash, filesize, filename, metadata };
  };

  const onRemoveFile = () => {
    files = [];
    fileData = {};
  };

  onMount(async () => {
    documentList = await CampaignsService.getDocumentList();
    videoList = await CampaignsService.getVideoList();

    documentItems = documentList.map((item) => {
      return {
        label: item.rulename,
        value: item.rulename,
        ...item
      };
    });

    videoItems = videoList.map((item) => {
      return {
        label: item.rulename,
        value: item.rulename,
        ...item
      };
    });
  });

  if (params) {
    onParamsChange(params);
  }
</script>

<div class="section">
  <div class="campaign-details">
    <div class="campaign-item">
      <div class="campaign-info">
        <b>{$format('label.CLICK_ACTION')}</b>
        <p>{$format('label.DESCRIPTION.CLICK_ACTION')}</p>
      </div>
      <div class="campaign-actions click-action">
        <ButtonGroup>
          <Button active={linktype === LINK_TYPES.url.id} on:click={(e) => onClickAction(e)}
            >{$format(LINK_TYPES.url.label)}</Button
          >
          <Button active={linktype === LINK_TYPES.doc.id} on:click={(e) => onClickAction(e)}
            >{$format(LINK_TYPES.doc.label)}</Button
          >
          <Button active={linktype === LINK_TYPES.video.id} on:click={(e) => onClickAction(e)}
            >{$format(LINK_TYPES.video.label)}</Button
          >
        </ButtonGroup>

        {#if linktype === LINK_TYPES.url.id}
          <Input
            type="text"
            bind:value={url}
            invalid={validationError && url.length}
            feedback={$format(errorMessage)}
            placeholder={$format('label.ENTER_URL')}
            on:input={onValidation}
            on:blur={onUrlChange}
          />
        {:else if linktype === LINK_TYPES.doc.id}
          <Select
            bind:items={documentItems}
            bind:value={selectedDocuments}
            placeholder={$format('label.PLACEHOLDER.SELECT_DOCUMENT')}
            class="select-element"
            clearable={false}
            showChevron
          >
            <svelte:fragment slot="item" let:item>
              <div class="item">
                <div class="item-detail">
                  <div class="image">
                    <img
                      src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                      style="background: url(https://www.sendergen.com/v2/view/{item.hash}/thumbnail.gif) no-repeat center center;"
                      alt={item.filename}
                    />
                  </div>
                  <div class="info">
                    <b>{item.rulename}</b>
                    <small>{$format('label.FILE_SIZE')} {formatBytes(item.filesize)}</small>
                  </div>
                </div>
              </div>
            </svelte:fragment>
          </Select>
        {:else if linktype === LINK_TYPES.video.id}
          <Select
            bind:items={videoItems}
            bind:value={selectedVideos}
            placeholder={$format('label.PLACEHOLDER.SELECT_VIDEO')}
            class="select-element"
            clearable={false}
            showChevron
          >
            <svelte:fragment slot="item" let:item>
              <div class="item">
                <div class="item-detail">
                  <div class="image">
                    <img
                      src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                      style="background: url({item.thumbnail}) no-repeat center center;"
                      alt={item.title}
                    />
                  </div>
                  <div class="info">
                    <b>{item.rulename}</b>
                    <small>{$format('label.LENGTH')} {secondsToHms(item.duration)}</small>
                  </div>
                </div>
              </div>
            </svelte:fragment>
          </Select>
        {/if}
      </div>
    </div>
    {#if linktype === LINK_TYPES.url.id && url.length && !validationError}
      <div class="campaign-item">
        <div class="campaign-info">
          <b>{$format('label.UTM_PARAMETERS')}</b>
          <p>{$format('label.DESCRIPTION.UTM_PARAMETERS')}</p>
        </div>
        <div class="campaign-actions">
          <QueryParams bind:params onChange={onParamsChange} />
        </div>
      </div>
    {/if}
    <div class="campaign-item">
      <div class="campaign-info">
        <b>{$format('label.AD_TYPE')}</b>
        <p>
          {@html $format('label.DESCRIPTION.AD_TYPE_CHOICE')}
        </p>

        {#if adtype === AD_TYPES.image.id}
          <br />
          <p>
            {@html $format('label.DESCRIPTION.AD_TYPE_IMAGE')}
          </p>
        {/if}
      </div>
      <div class="campaign-actions ad">
        <ButtonGroup>
          <Button id="type-text" active={adtype === AD_TYPES.text.id} on:click={(e) => onAdType(e)}
            >{$format(AD_TYPES.text.label)}</Button
          >
          <Button id="type-image" active={adtype === AD_TYPES.image.id} on:click={(e) => onAdType(e)}
            >{$format(AD_TYPES.image.label)}</Button
          >
        </ButtonGroup>

        {#if adtype === AD_TYPES.text.id}
          <div class="text-ad">
            <Input type="text" bind:value={textString} placeholder={$format('label.PLACEHOLDER.AD_TEXT')} />

            <Input
              class="css-input"
              type="text"
              bind:value={textCss}
              placeholder={$format('label.PLACEHOLDER.AD_CSS')}
            />
          </div>
        {:else if adtype === AD_TYPES.image.id}
          <div class="image-ad">
            <label class="btn btn-secondary btn-sm" for="image-single">{$format('label.CHOOSE_FILE')}</label>
            <input
              id="image-single"
              name="image-single"
              hidden
              type="file"
              bind:files
              on:change={onFileUpload}
              accept=".png, .jpg, .jpeg, .gif"
            />

            {#if files.length}
              <div class="single-image">
                {#each Array.from(files) as file, index}
                  <div class="file-item">
                    <div class="image-preview">
                      <img src={URL.createObjectURL(file)} alt={file.name} />
                      <div class="details">
                        <small>{file.name}</small>
                        <small>{file.type} - ({formatBytes(file.size)})</small>
                      </div>
                    </div>
                    <div class="icon-actions">
                      <Icon name="trash-2" class="pointer" role="button" tabindex="0" on:click={onRemoveFile} />
                    </div>
                  </div>
                {/each}
              </div>
            {/if}

            <Input
              class="alt-text-input"
              type="text"
              bind:value={alttext}
              placeholder={$format('label.PLACEHOLDER.AD_IMAGE_ALT_TEXT')}
            />
          </div>
        {/if}
      </div>
    </div>

    {#if adtype === AD_TYPES.image.id}
      <div class="campaign-item">
        <div class="campaign-info">
          <b>{$format('label.INLINE_IMAGE')}</b>
          <p>{$format('label.DESCRIPTION.INLINE_IMAGE')}</p>
        </div>
        <div class="campaign-actions">
          <Switch bind:checked={inline} />
        </div>
      </div>
      <div class="campaign-item">
        <div class="campaign-info">
          <b>{$format('label.HIGH_RESOLUTION_IMAGE')}</b>
          <p>{$format('label.DESCRIPTION.HIGH_RESOLUTION_IMAGE')}</p>
        </div>
        <div class="campaign-actions">
          <Switch bind:checked={is2x} />
        </div>
      </div>
      <div class="campaign-item">
        <div class="campaign-info">
          <b>{$format('label.PLACEHOLDER_IMAGE')}</b>
          <p>{$format('label.DESCRIPTION.PLACEHOLDER_IMAGE')}</p>
        </div>
        <div class="campaign-actions">
          <Switch bind:checked={isPlaceholder} />
        </div>
      </div>
    {/if}

    <div class="campaign-item">
      <div class="campaign-info">
        <b>{$format('label.ALLOW_INTERNAL')}</b>
        <p>{$format('label.DESCRIPTION.ALLOW_INTERNAL')}</p>
      </div>
      <div class="campaign-actions">
        <Switch bind:checked={countInternal} />
      </div>
    </div>
    <div class="campaign-item">
      <div class="campaign-info">
        <b>{$format('label.DISABLE_VIEW_TRACKING')}</b>
        <p>{$format('label.DESCRIPTION.DISABLE_VIEW_TRACKING')}</p>
      </div>
      <div class="campaign-actions">
        <Switch bind:checked={disableViews} />
      </div>
    </div>
  </div>
</div>

<style lang="scss">
  .campaign-actions {
    &.click-action {
      :global(.btn-group) {
        width: 100%;
      }

      align-items: normal;
      flex-direction: column;

      .list-item {
        .item {
          &.active {
            b {
              color: var(--color-white);
            }

            small {
              color: var(--color-gray-400);
            }

            .name {
              color: var(--color-gray-400);
            }
          }
        }
      }

      .item-detail {
        align-items: center;
        display: block;

        .info {
          align-items: start;
          display: flex;
          flex-direction: column;
          justify-content: center;

          b {
            color: var(--color-gray-900);
            text-align: center;
            text-wrap: wrap;
          }

          small {
            color: var(--color-gray-600);
            display: block;
            margin-top: var(--margin-xsmall);
          }
        }

        .image {
          align-items: start;
          display: flex;
          justify-content: start;
          margin-bottom: var(--margin-large);

          .name {
            color: var(--color-gray-600);
            margin-top: var(--margin-xsmall);
            text-align: center;
            text-wrap: wrap;
          }
        }
      }

      .image {
        img {
          background-size: contain !important;
          border: 1px solid var(--border-color);
          border-radius: var(--border-radius-base);
          display: block;
          height: 120px;
          width: auto;
        }
      }
    }

    &.ad {
      align-items: normal;
      flex-direction: column;
      justify-content: normal;

      .text-ad {
        :global(.css-input) {
          margin-top: var(--margin-large) !important;
        }
        width: 275px;
      }

      .image-ad {
        align-items: flex-start;
        flex-direction: row;

        :global(.alt-text-input) {
          margin-top: var(--margin-large) !important;
        }

        label {
          width: 100%;
        }

        .single-image {
          width: 100%;

          .file-item {
            align-items: center;
            display: flex;
            gap: 8px;
            margin-top: var(--margin-large);
            text-align: left;

            .image-preview {
              border-radius: var(--border-radius-large);
              border: 1px solid var(--border-color);
              min-height: 100px;
              padding: var(--padding-base);
              max-width: 600px;

              img {
                display: block;
                height: auto;
                width: 100%;
              }

              .details {
                align-items: center;
                border-top: 1px solid var(--border-color);
                color: var(--color-gray-700);
                display: flex;
                justify-content: space-between;
                margin-top: var(--margin-base);
                padding: var(--padding-base) 0 0;
              }
            }

            .icon-actions {
              display: flex;
              flex: 1;
            }
          }
        }
      }
    }

    :global(.fields) {
      margin-top: none;
    }
  }
</style>
